import type { Catalog, Obligation } from "../engine/types"
import { BAUSTEINE } from "./bausteine"
import { ASPIRATION_REQUIREMENTS, ROLE_REQUIREMENTS } from "./roles"
import { SKILLS } from "./skills"

/**
 * Mandatory training obligations — the regulatory spine.
 *
 * This is the file that carries the product's core claim (plan §2, delta #3):
 * a bank curriculum is not "your top skill gaps", it is "what the regulator
 * requires of someone in your seat, by when" — and the development plan is
 * what fits in the hours that remain.
 *
 * Modelled as its own concept rather than as a skill gap because obligations
 * behave differently at every step: they cannot be dropped by the budget, they
 * carry a hard deadline, and they RE-APPEAR on expiry even when the underlying
 * skill is long since held.
 *
 * SYNTHETIC but modelled on real Swiss obligations. A production version would
 * be sourced from Compliance's own role→Pflichtschulung matrix — plan §12 Q4.
 */
export const OBLIGATIONS: Obligation[] = [
    {
        id: "obl.fidleg",
        label: "FIDLEG-Schulungspflicht für Kundenberatende",
        basis: "FIDLEG Art. 6 / FIDLEV — Aus- und Weiterbildungspflicht",
        appliesToRoles: [
            "kundenberater",
            "anlageberater",
            "finanzplaner",
            "hypothekarberater",
            "firmenkundenberater",
        ],
        bausteinId: "fidleg-refresher",
        validForMonths: 12,
        leadTimeDays: 60,
    },
    {
        id: "obl.gwg",
        label: "GwG-Schulungspflicht",
        basis: "GwG Art. 8 / GwV-FINMA — Ausbildung des Personals",
        appliesToRoles: [
            "kundenberater",
            "anlageberater",
            "finanzplaner",
            "hypothekarberater",
            "firmenkundenberater",
            "kreditanalyst",
            "compliance-officer",
            "operations",
        ],
        bausteinId: "gwg-refresher",
        validForMonths: 12,
        leadTimeDays: 60,
    },
    {
        id: "obl.vsb",
        label: "VSB-Sorgfaltspflichten",
        basis: "VSB 20 — Sorgfaltspflichten bei der Aufnahme von Geschäftsbeziehungen",
        appliesToRoles: ["kundenberater", "firmenkundenberater", "compliance-officer", "operations"],
        bausteinId: "vsb-kyc",
        validForMonths: 36,
        leadTimeDays: 90,
    },
    {
        id: "obl.datenschutz",
        label: "Datenschutzschulung",
        basis: "revDSG — Sensibilisierung der Mitarbeitenden",
        appliesToRoles: [
            "kundenberater",
            "anlageberater",
            "finanzplaner",
            "hypothekarberater",
            "kreditanalyst",
            "firmenkundenberater",
            "compliance-officer",
            "risk-manager",
            "it-engineer",
            "it-security",
            "projektleiter",
            "operations",
            "hr-specialist",
            "teamleiter",
            "lernender",
        ],
        bausteinId: "datenschutz",
        validForMonths: 24,
        leadTimeDays: 60,
    },
    {
        id: "obl.infosec",
        label: "Informationssicherheits-Schulung",
        basis: "FINMA-RS 23/1 Operationelle Risiken — Sensibilisierung",
        appliesToRoles: [
            "kundenberater",
            "anlageberater",
            "finanzplaner",
            "hypothekarberater",
            "kreditanalyst",
            "firmenkundenberater",
            "compliance-officer",
            "risk-manager",
            "it-engineer",
            "it-security",
            "projektleiter",
            "operations",
            "hr-specialist",
            "teamleiter",
            "lernender",
        ],
        bausteinId: "infosec-basis",
        validForMonths: 12,
        leadTimeDays: 45,
    },
    {
        id: "obl.ai",
        label: "Pflichtkurs generative AI",
        basis: "Weisung A-035 Nutzungsbedingungen generativer KI",
        appliesToRoles: [
            "kundenberater",
            "anlageberater",
            "finanzplaner",
            "hypothekarberater",
            "kreditanalyst",
            "firmenkundenberater",
            "compliance-officer",
            "risk-manager",
            "it-engineer",
            "it-security",
            "projektleiter",
            "operations",
            "hr-specialist",
            "teamleiter",
            "lernender",
        ],
        bausteinId: "ai-risiko",
        validForMonths: 24,
        leadTimeDays: 45,
    },
    {
        id: "obl.sanktionen",
        label: "Sanktionsschulung",
        basis: "Embargogesetz / SECO — interne Weisung Sanktionen",
        appliesToRoles: ["firmenkundenberater", "compliance-officer", "operations"],
        bausteinId: "sanktionen",
        validForMonths: 24,
        leadTimeDays: 60,
    },
    {
        id: "obl.kreditkompetenz",
        label: "Kreditkompetenz-Schulung",
        basis: "Interne Kompetenzordnung Kredit — Voraussetzung für Unterschriftsberechtigung",
        appliesToRoles: [],
        appliesToSigningAuthority: ["Kreditkompetenz A", "Kreditkompetenz B"],
        bausteinId: "kreditrisiko-fortgeschritten",
        validForMonths: 36,
        leadTimeDays: 90,
    },
    {
        id: "obl.arbeitsrecht",
        label: "Arbeitsrecht für Führungskräfte",
        basis: "Interne Weisung Führung — Pflichtschulung bei Führungsübernahme",
        appliesToRoles: ["teamleiter"],
        bausteinId: "arbeitsrecht",
        validForMonths: null,
        leadTimeDays: 90,
    },
]

/**
 * The assembled demo catalog. A single object because the engine takes the
 * catalog as a parameter — it never reaches for a module-level import, which
 * is what keeps it portable and testable with a fixture catalog.
 */
export const DEMO_CATALOG: Catalog = {
    skills: SKILLS,
    bausteine: BAUSTEINE,
    obligations: OBLIGATIONS,
    roleRequirements: ROLE_REQUIREMENTS,
    aspirationRequirements: ASPIRATION_REQUIREMENTS,
}
